import React, { useEffect, useState } from "react";
import { API_URL } from "../config";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import { showToast } from "../components/toast";
import { hasPermission, getRoleDetails, getRoleUserid } from "../helpers/permission.js";

const AddPaidServiceOrder = ({
  isOpen,
  onClose,
  onSubmit,
  dropdownOptions, // ⬅️ still here but we will fetch if missing
  subscriberId,
}) => {
  const token = localStorage.getItem("authToken");
  const [loading, setLoading] = useState(false);
  const hasAddonPermission = Boolean(
    hasPermission("add_on_order", "view") ||
      hasPermission("add_on_order", "add") ||
      hasPermission("orders.add_on_order", "view") ||
      hasPermission("orders.add_on_order", "add")
  );

  const [activeTab, setActiveTab] = useState("paid"); // "paid" or "addon"
  const [orderList, setOrderList] = useState([]);
  const [latestOrderList, setLatestOrderList] = useState([]);
  const [localDropdownOptions, setLocalDropdownOptions] = useState({
    serviceList: [],
    paidTradesList: [],
    distributorList: [],
  });

  const [paidServiceData, setPaidServiceData] = useState({
    orderid: "",
    serviceid: "",
    trade_purchase: "",
    amount: "",
    activation_date: "",
    referralcode: "",
  });

  // 🔹 set default activation date (tomorrow)
  useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setPaidServiceData((prev) => ({ ...prev, activation_date: tomorrow }));
  }, []);

  // 🔹 Fetch unmapped orders for subscriber
  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}orders/unmapped-orders/${subscriberId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.success) {
        setOrderList(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching unmapped orders:", error);
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Fetch latest 3 orders for subscriber
  const fetchLatestOrders = async () => {
    try {
      const response = await axios.get(
        `${API_URL}orders/latest-orders/${subscriberId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.success) {
        setLatestOrderList(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching latest orders:", error);
    }
  };

  // 🔹 Fetch service + trades list + distributor list
  const fetchServices = async () => {
    try {
      const response = await axios.get(
        `${API_URL}subscriber/get-dropdown-other`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data?.success && response.data?.data) {
        setLocalDropdownOptions({
          serviceList: response.data.data.servicelist || [],
          paidTradesList: response.data.data.paidtradeList || [],
          distributorList: response.data.data.distributorList || [],
        });
      }
    } catch (error) {
      console.error("Error fetching services:", error);
    }
  };

  useEffect(() => {
    if (!hasAddonPermission && activeTab === "addon") {
      setActiveTab("paid");
    }
  }, [hasAddonPermission, activeTab]);

  useEffect(() => {
    if (subscriberId && isOpen) {
      fetchOrders();
      if (hasAddonPermission) {
        fetchLatestOrders();
      }
      fetchServices();
    }
  }, [subscriberId, isOpen, hasAddonPermission]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "addon") {
      setPaidServiceData((prev) => ({
        ...prev,
        orderid: "",
        amount: 0,
      }));
    } else {
      setPaidServiceData((prev) => ({
        ...prev,
        orderid: "",
        amount: "",
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "orderid") {
      if (activeTab === "addon") {
        setPaidServiceData((prev) => ({
          ...prev,
          [name]: value,
          amount: 0,
        }));
      } else {
        const selectedOrder = orderList.find(
          (order) => order.id === parseInt(value)
        );
        setPaidServiceData((prev) => ({
          ...prev,
          [name]: value,
          amount: selectedOrder?.amount_payable || "",
        }));
      }
    } else {
      setPaidServiceData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleDateChange = (name, date) => {
    setPaidServiceData((prev) => ({ ...prev, [name]: date }));
  };

  const handleReset = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setPaidServiceData({
      orderid: "",
      serviceid: "",
      trade_purchase: "",
      amount: "",
      activation_date: tomorrow,
      referralcode: "",
    });
    setActiveTab("paid");
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!paidServiceData.orderid) {
      showToast.warning("please select order");
      return;
    }

    if (!paidServiceData.serviceid) {
      showToast.warning("please select service");
      return;
    }

    if (!paidServiceData.trade_purchase) {
      showToast.warning("please select trades purchase");
      return;
    }

    if (
      activeTab !== "addon" &&
      (paidServiceData.amount === "" ||
        paidServiceData.amount === null ||
        paidServiceData.amount === undefined)
    ) {
      showToast.warning("amount is required");
      return;
    }

    if (!paidServiceData.activation_date) {
      showToast.warning("please select activation date");
      return;
    }

    const payload = {
      ...paidServiceData,
      amount: activeTab === "addon" ? 0 : paidServiceData.amount,
      addon: activeTab === "addon" ? 1 : 0,
    };

    onSubmit(payload);
    handleReset();
    onClose();
  };

  if (!isOpen) return null;

  // 🔹 Prefer local fetched dropdowns, fallback to parent props
  const finalDropdownOptions = {
    serviceList:
      localDropdownOptions.serviceList.length > 0
        ? localDropdownOptions.serviceList
        : dropdownOptions?.serviceList || [],
    paidTradesList:
      localDropdownOptions.paidTradesList.length > 0
        ? localDropdownOptions.paidTradesList
        : dropdownOptions?.paidTradesList || [],
    distributorList:
      localDropdownOptions.distributorList.length > 0
        ? localDropdownOptions.distributorList
        : dropdownOptions?.distributorList || [],
  };

  const isDistributorVisible = () => {
    return hasPermission("subscriber_distributor", "view");
  };

  const isDistributorEditable = () => {
    return hasPermission("subscriber_distributor", "edit");
  };

  const currentOrders = activeTab === "addon" ? latestOrderList : orderList;

  return (
    <div
      className="modal fade show"
      style={{ display: "block" }}
      tabIndex="-1"
      role="dialog"
      aria-labelledby="exampleModalLabel"
      aria-hidden="true"
    >
      <div className="modal-dialog" style={{ maxWidth: "32rem" }}>
        <div className="modal-content">
          <div id="myModalContent">
            <div className="modal-header">
              <div className="col-md-11">
                <h5 className="modal-title" style={{ fontWeight: "bold" }}>
                  Add Paid Service For Order
                </h5>
              </div>
              <button
                type="button"
                className="btn btn-default"
                onClick={handleClose}
              >
                &times;
              </button>
              <div className="clearfix"></div>
            </div>

            <div className="modal-body">
              <form onSubmit={handleSubmit}>
                <div className="panel-body">
                  <div className="row">
                    <table className="table table-condensed table-hover bg-white usertable">
                      <tbody>
                        {hasAddonPermission && (
                          <tr>
                            <td colSpan="2">
                              <div className="d-flex align-items-center ms-2 p-2">
                                <input
                                  type="radio"
                                  className="me-2"
                                  name="order_tab"
                                  id="order_tab_paid"
                                  value="paid"
                                  checked={activeTab === "paid"}
                                  onChange={() => handleTabChange("paid")}
                                  style={{ cursor: "pointer", width: "auto" }}
                                />
                                <label
                                  htmlFor="order_tab_paid"
                                  className="m-0 me-4"
                                  style={{ cursor: "pointer", fontWeight: "bold" }}
                                >
                                  Paid Service Order
                                </label>

                                <input
                                  type="radio"
                                  className="me-2"
                                  name="order_tab"
                                  id="order_tab_addon"
                                  value="addon"
                                  checked={activeTab === "addon"}
                                  onChange={() => handleTabChange("addon")}
                                  style={{ cursor: "pointer", width: "auto" }}
                                />
                                <label
                                  htmlFor="order_tab_addon"
                                  className="m-0"
                                  style={{ cursor: "pointer", fontWeight: "bold" }}
                                >
                                  Add On Order
                                </label>
                              </div>
                            </td>
                          </tr>
                        )}
                        <tr>
                          <td>
                            Orders <span style={{ color: "red" }}>*</span>
                          </td>
                          <td>
                            <select
                              name="orderid"
                              value={paidServiceData.orderid}
                              className="form-control"
                              onChange={handleChange}
                              required
                            >
                              <option value="">Select order</option>
                              {currentOrders.map((option, index) => (
                                <option key={index} value={option.id}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            {activeTab === "addon" && currentOrders.length === 0 && (
                              <small className="text-muted d-block mt-1">
                                No previous orders found for this subscriber.
                              </small>
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td>
                            Service <span style={{ color: "red" }}>*</span>
                          </td>
                          <td>
                            <select
                              name="serviceid"
                              className="form-control"
                              value={paidServiceData.serviceid}
                              onChange={handleChange}
                              required
                            >
                              <option value="">- Select -</option>
                              {finalDropdownOptions?.serviceList?.map(
                                (option, index) => (
                                  <option key={index} value={option.id}>
                                    {option.service_name}
                                  </option>
                                )
                              )}
                            </select>
                          </td>
                        </tr>

                        <tr>
                          <td>
                            Trades Purchase{" "}
                            <span style={{ color: "red" }}>*</span>
                          </td>
                          <td>
                            <select
                              name="trade_purchase"
                              className="form-control"
                              value={paidServiceData.trade_purchase}
                              onChange={handleChange}
                              required
                            >
                              <option value="">- Select -</option>
                              {finalDropdownOptions?.paidTradesList?.map(
                                (option, index) => (
                                  <option key={index} value={option.pick_name}>
                                    {option.pick_name}
                                  </option>
                                )
                              )}
                            </select>
                          </td>
                        </tr>

                        <tr>
                          <td>
                            Amount <span style={{ color: "red" }}>*</span>
                          </td>
                          <td>
                            <input
                              type="text"
                              name="amount"
                              value={
                                activeTab === "addon"
                                  ? 0
                                  : paidServiceData.amount
                              }
                              onChange={handleChange}
                              placeholder="Enter Amount"
                              className="form-control"
                              required
                              disabled
                            />
                          </td>
                        </tr>

                        <tr>
                          <td>
                            Activation Date{" "}
                            <span style={{ color: "red" }}>*</span>
                          </td>
                          <td>
                            <DatePicker
                              selected={paidServiceData.activation_date}
                              onChange={(date) =>
                                handleDateChange("activation_date", date)
                              }
                              dateFormat="dd-MM-yyyy"
                              className="form-control"
                              placeholderText="Select Activation Date"
                            />
                          </td>
                        </tr>

                        {isDistributorVisible() && (
                          <tr>
                            <td>Distributor / Referral Code</td>
                            <td>
                              <select
                                name="referralcode"
                                className="form-control"
                                value={paidServiceData.referralcode}
                                onChange={handleChange}
                                disabled={!isDistributorEditable()}
                              >
                                <option value="">- Select Distributor -</option>
                                {finalDropdownOptions?.distributorList?.map(
                                  (item, index) => (
                                    <option
                                      key={index}
                                      value={item.referralcode || ""}
                                    >
                                      {item.user_fullname}{" "}
                                      {item.referralcode
                                        ? `(${item.referralcode})`
                                        : ""}
                                    </option>
                                  )
                                )}
                              </select>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>

                    <div
                      className="col-md-12 mt-3"
                      style={{ textAlign: "right" }}
                    >
                      <button
                        type="submit"
                        name="submit"
                        className="btn btn-primary me-2"
                      >
                        Submit
                      </button>
                      <button
                        type="button"
                        name="cancel"
                        className="btn btn-default"
                        onClick={handleClose}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPaidServiceOrder;
